import React, { PureComponent, ReactNode } from "react";
import { AppContextType } from "../../../AppContext";
import { AudioHistoryState } from "../../../statemachines/AudioHistoryState";
import MusicNote from "./MusicNote";
import MusicChord from "./MusicChord";
import "./HistoryMonitorColumn.css";
import { ChordInScaleEdit } from "../../../models/wasm.model";
import { MusicScale } from "../../../../wasm/pkg/wasm";

interface Props {
  audioHistory: AudioHistoryState;
  isCaptureActive: boolean;
  /** The slice of time to render */
  timeIndex: number;
  highlightColumn: boolean;
  onDeleteChord: (scaleId: string, tickTimeIndex: number, chordIndex: number) => void;
  onDeleteScale: (scaleId: string) => void;
  openChordPopup: (args: ChordPopupArgs) => void;
  onChooseChordForInstrumentation: (chord: ChordInScaleEdit, scale: MusicScale) => void;
  onTimerAxisNavigation: (timestamp: number) => void;
}
interface State {}

export type ChordPopupArgs = {
  scaleId: string;
  timeIndex: number;
  chordIndex: number;
  clickX: number;
  clickY: number;
};

export default class HistoryMonitorColumn extends PureComponent<Props, State> {
  declare context: React.ContextType<typeof AppContextType>;
  constructor(props: Props) {
    super(props);

    this.state = {};
  }

  curryHandleChordExpand =
    (scaleId: string, timeIndex: number, chordIndex: number) =>
    ({ x, y }: { x: number; y: number }) => {
      this.props.openChordPopup({
        scaleId,
        timeIndex,
        chordIndex,
        clickX: x,
        clickY: y,
      });
    };

  curryHandleChordDelete = (scaleId: string, timeIndex: number, chordIndex: number) => () => {
    this.props.onDeleteChord(scaleId, timeIndex, chordIndex);
  };

  curryChooseChordForInstrumentation = (chord: ChordInScaleEdit, scaleId: string) => () => {
    const scale = this.props.audioHistory.chordsInScales[scaleId].scale;
    this.props.onChooseChordForInstrumentation(chord, scale);
  };

  curryHandleTimerAxisNavigation = (timestamp: number) => () => {
    if (!this.props.isCaptureActive) {
      this.props.onTimerAxisNavigation(timestamp);
    }
  };

  render(): ReactNode {
    const { audioHistory, timeIndex, highlightColumn } = this.props;
    const scaleIds = Object.keys(audioHistory.chordsInScales)?.toSorted() || [];
    const { primaryScale, primaryScaleId } = audioHistory;
    const timestamp = audioHistory.timestamps[timeIndex] / 1000;
    const timestampDisplay = `${Math.floor(timestamp / 60)}:${(timestamp % 60).toFixed(0).padStart(2, "0")}`;
    const timestampId = `tick-${timestamp}`;
    const hideOctave = this.context.chordHistoryOptions.noteRow.hideNoteOctave;
    const hideNotes = this.context.chordHistoryOptions.noteRow.hideNotes;
    // The user may jump around within the song.  Only highlight the newest column if it's not last chronologically.
    const highlightedColumnClass = highlightColumn ? "highlight-column" : "";
    return (
      <div className={`audio-history-scroll-column ${highlightedColumnClass}`}>
        <div
          className="timer-tick-axis-count"
          id={timestampId}
          onClick={this.curryHandleTimerAxisNavigation(audioHistory.timestamps[timeIndex])}
        >
          {timestamp === Math.floor(timestamp) ? timestampDisplay : ""}
        </div>
        {!hideNotes && (
          <div className="history-notes-for-one-tick">
            {(audioHistory.noteHistory[timeIndex] || []).map((note) => {
              const weightOpacity = note.sum_weights / audioHistory.weightHistory.biggestSumWeightYet;
              const key = `${timestamp}${note.identity}note-per-tick`;
              return (
                <div key={key} className="history-single-note">
                  <MusicNote
                    note_id={note.identity}
                    octave={note.primary_octave}
                    hideOctave={hideOctave}
                    scale={primaryScale}
                    highlightWrongNotes
                    weightOpacity={weightOpacity}
                  />
                </div>
              );
            })}
          </div>
        )}
        {scaleIds.length === 0 && <div className="history-chords-per-key-for-one-tick"></div>}
        {scaleIds.map((scaleId, index) => {
          // let hideScale = audioHistory.chordsInScales[scaleId].hideRuledOutScale;
          const rowNum = index + 1;
          const rowClasses = `history-scales-row${rowNum > 9 ? "" : 0}${rowNum} history-chords-per-key-for-one-tick`;
          // const isTopmostAvailableChord =
          // need to know whether chords exist at each index for this scale (and for other scales)
          // const isMostLikely = (scaleId === primaryScaleId || topmostScaleIdWithChordsPerTick[timeIndex]
          return (
            <div key={scaleId} className={rowClasses}>
              {audioHistory.chordsInScales[scaleId].chordHistory[timeIndex]?.slice(0, 7).map((c, chordIndex) => {
                const key = `${timestamp}${c.chord_type}${c.root_note.note_id}`;
                const isMostLikely =
                  chordIndex === 0 &&
                  (scaleId === primaryScaleId ||
                    ((!primaryScaleId || audioHistory.chordsInScales[primaryScaleId].chordHistory[timeIndex].length === 0) &&
                      audioHistory.timestampMeta[timeIndex].mostLikelyScaleId === scaleId));
                return (
                  <MusicChord
                    key={key}
                    chord={c}
                    onExpand={this.curryHandleChordExpand(scaleId, timeIndex, chordIndex)}
                    onDelete={this.curryHandleChordDelete(scaleId, timeIndex, chordIndex)}
                    onChooseForInstrumentation={this.curryChooseChordForInstrumentation(c, scaleId)}
                    isMostLikely={isMostLikely}
                  />
                );
              })}
            </div>
          );
        })}
      </div>
    );
  }
}

HistoryMonitorColumn.contextType = AppContextType;
